import { useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import type { CrmContact, CrmStageCount } from '../../types/crm';
import { CrmWhatsAppTemplatePopover } from './CrmWhatsAppTemplatePopover';
import { validatePhone } from '../../utils/phoneUtils';

interface CrmKanbanBoardProps {
  stages: CrmStageCount[];
  contacts: CrmContact[];
  onOpenContact: (contact: CrmContact) => void;
  onMoveContact: (contactId: string, stage: string) => void;
  companyName?: string;
}

const formatDate = (value?: string | null) => {
  if (!value) return 'Sem data';
  return new Date(value).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit'
  });
};

export function CrmKanbanBoard({ stages, contacts, onOpenContact, onMoveContact, companyName }: CrmKanbanBoardProps) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [collapsedStages, setCollapsedStages] = useState<Record<string, boolean>>({});
  const [quickAction, setQuickAction] = useState<{
    contactId: string;
    stageKey: string;
    stageLabel: string;
    anchorRect: DOMRect;
  } | null>(null);

  const grouped = stages.map((stage) => ({
    ...stage,
    contacts: contacts.filter((contact) => contact.statusJornada === stage.key)
  }));

  const collapsedCount = useMemo(
    () => grouped.filter((stage) => collapsedStages[stage.key]).length,
    [collapsedStages, grouped]
  );

  const toggleStage = (stageKey: string) => {
    setCollapsedStages((prev) => ({
      ...prev,
      [stageKey]: !prev[stageKey]
    }));
  };

  const expandAllStages = () => {
    setCollapsedStages({});
  };

  const collapseAllStages = () => {
    setCollapsedStages(
      grouped.reduce<Record<string, boolean>>((acc, stage) => {
        acc[stage.key] = true;
        return acc;
      }, {})
    );
  };

  const scrollColumns = (direction: 'left' | 'right') => {
    scrollRef.current?.scrollBy({
      left: direction === 'left' ? -420 : 420,
      behavior: 'smooth'
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 rounded-3xl border border-slate-200 bg-slate-50 px-4 py-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="text-sm font-semibold text-slate-900">Visualização do funil</p>
          <p className="text-xs text-slate-500">
            Role horizontalmente, recolha etapas menos usadas e expanda quando quiser.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => scrollColumns('left')}
            className="rounded-full border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-100"
          >
            ← Ver anteriores
          </button>
          <button
            type="button"
            onClick={() => scrollColumns('right')}
            className="rounded-full border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-100"
          >
            Ver próximas →
          </button>
          <button
            type="button"
            onClick={expandAllStages}
            className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-800 hover:bg-emerald-100"
          >
            Expandir todas
          </button>
          <button
            type="button"
            onClick={collapseAllStages}
            className="rounded-full border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800 hover:bg-amber-100"
          >
            Recolher todas
          </button>
        </div>
      </div>

      <div className="flex items-center justify-between text-xs text-slate-500">
        <span>{grouped.length} etapas no funil</span>
        <span>{collapsedCount} recolhida(s)</span>
      </div>

      <div ref={scrollRef} className="overflow-x-auto pb-3">
        <div className="flex min-w-max gap-4 pr-2">
        {grouped.map((stage) => (
          <section
            key={stage.key}
            className={`rounded-3xl border border-slate-200 bg-slate-50/80 p-4 transition-all duration-200 ${
              collapsedStages[stage.key] ? 'w-[92px]' : 'w-[340px]'
            }`}
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => {
              event.preventDefault();
              const contactId = event.dataTransfer.getData('text/plain');
              if (contactId) {
                onMoveContact(contactId, stage.key);
              }
            }}
          >
            <div className="mb-4 flex items-center justify-between">
              <div className={collapsedStages[stage.key] ? 'min-w-0' : ''}>
                <p className={`font-semibold text-slate-900 ${collapsedStages[stage.key] ? 'text-xs [writing-mode:vertical-rl] rotate-180' : 'text-sm'}`}>
                  {stage.label}
                </p>
                {!collapsedStages[stage.key] ? (
                  <p className="text-xs text-slate-500">{stage.contacts.length} contatos</p>
                ) : null}
              </div>
              <div className={`flex ${collapsedStages[stage.key] ? 'flex-col items-center gap-2' : 'items-center gap-2'}`}>
                <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-600 shadow-sm">
                  {stage.count}
                </span>
                <button
                  type="button"
                  onClick={() => toggleStage(stage.key)}
                  className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-600 shadow-sm hover:bg-slate-100"
                  title={collapsedStages[stage.key] ? 'Expandir coluna' : 'Recolher coluna'}
                >
                  {collapsedStages[stage.key] ? '›' : '‹'}
                </button>
              </div>
            </div>

            {collapsedStages[stage.key] ? (
              <div className="flex min-h-[220px] items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-white/80 px-2 text-center text-[11px] text-slate-400">
                Coluna recolhida
              </div>
            ) : (
            <div className="max-h-[68vh] space-y-3 overflow-y-auto pr-1">
              {stage.contacts.map((contact) => (
                <article
                  key={contact.id}
                  role="button"
                  tabIndex={0}
                  draggable
                  onDragStart={(event) => {
                    event.dataTransfer.setData('text/plain', contact.id);
                  }}
                  onClick={() => onOpenContact(contact)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      onOpenContact(contact);
                    }
                  }}
                  className="group w-full rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-md"
                >
                  <div className="mb-3 flex items-start justify-between gap-4 pr-1">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-900">{contact.nome}</p>
                      <p className="text-xs text-slate-500">{contact.telefone}</p>
                      <p className="text-xs text-slate-500">{contact.email || 'Sem e-mail'}</p>
                    </div>
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        if (!validatePhone(contact.telefone)) {
                          toast.error('Este contato não possui um número de WhatsApp cadastrado.');
                          return;
                        }
                        setQuickAction({
                          contactId: contact.id,
                          stageKey: stage.key,
                          stageLabel: stage.label,
                          anchorRect: event.currentTarget.getBoundingClientRect()
                        });
                      }}
                      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 shadow-sm transition hover:bg-emerald-100"
                      title="Abrir templates de WhatsApp"
                      aria-label="Abrir templates de WhatsApp"
                    >
                      <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        <path d="M20.52 3.48A11.88 11.88 0 0 0 12.03 0C5.47 0 .12 5.35.12 11.9c0 2.1.55 4.16 1.6 6L0 24l6.26-1.65a11.9 11.9 0 0 0 5.77 1.48h.01c6.56 0 11.9-5.35 11.9-11.9 0-3.18-1.24-6.17-3.42-8.45ZM12.04 21.6a9.9 9.9 0 0 1-5.05-1.38l-.36-.21-3.72.98.99-3.62-.23-.37a9.9 9.9 0 0 1-1.52-5.29c0-5.47 4.46-9.93 9.94-9.93 2.65 0 5.13 1.03 7 2.9a9.87 9.87 0 0 1 2.94 7.03c0 5.48-4.46 9.9-9.99 9.9Zm5.78-7.47c-.32-.16-1.9-.94-2.2-1.05-.29-.1-.5-.16-.71.16-.2.32-.82 1.05-1 .25-.18-.34-.89-1.3-1.55-2.09-.55-.72-1.08-.74-1.31-.82-.24-.1-.48-.08-.65.08-.18.16-.69.68-.69 1.66 0 .98.71 1.92.8 2.05.1.13 1.42 2.16 3.45 3.03.48.2.85.33 1.15.42.49.16.94.14 1.29.08.4-.05 1.25-.5 1.43-.99.18-.49.18-.9.13-.99-.06-.1-.21-.16-.54-.32Z" />
                      </svg>
                    </button>
                  </div>
                  <span className="rounded-full bg-slate-100 px-2 py-1 text-[11px] font-medium text-slate-600">
                    {contact.nivelInteresse || 'Neutro'}
                  </span>

                  <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-slate-500">
                    <div>
                      <span className="block text-[11px] uppercase tracking-wide text-slate-400">Interesse</span>
                      <span>{contact.interestLabel || contact.interessePrincipal || 'Nao definido'}</span>
                    </div>
                    <div>
                      <span className="block text-[11px] uppercase tracking-wide text-slate-400">Responsavel</span>
                      <span>{contact.responsavel || 'Nao definido'}</span>
                    </div>
                    <div>
                      <span className="block text-[11px] uppercase tracking-wide text-slate-400">Ultimo contato</span>
                      <span>{formatDate(contact.ultimoContato || contact.lastInteraction?.criadoEm)}</span>
                    </div>
                    <div>
                      <span className="block text-[11px] uppercase tracking-wide text-slate-400">Proxima acao</span>
                      <span>{formatDate(contact.proximaAcao || contact.nextTask?.dataAgendada)}</span>
                    </div>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-1">
                    {(contact.tagItems || []).slice(0, 3).map((tag) => (
                      <span
                        key={tag.id}
                        className="rounded-full px-2 py-1 text-[11px] font-medium text-white"
                        style={{ backgroundColor: tag.color }}
                      >
                        {tag.name}
                      </span>
                    ))}
                  </div>

                  {quickAction?.contactId === contact.id ? (
                    <CrmWhatsAppTemplatePopover
                      isOpen
                      contact={contact}
                      stageKey={quickAction.stageKey}
                      stageLabel={quickAction.stageLabel}
                      companyName={companyName}
                      anchorRect={quickAction.anchorRect}
                      onClose={() => setQuickAction(null)}
                    />
                  ) : null}
                </article>
              ))}

              {stage.contacts.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-slate-200 bg-white/70 p-5 text-center text-sm text-slate-400">
                  Nenhum contato nesta etapa.
                </div>
              ) : null}
            </div>
            )}
          </section>
        ))}
        </div>
      </div>
    </div>
  );
}
